/**
 * §13.1.4 — in-run HUD.
 *
 * ## Anchoring (AMD-6, supersedes §13.1.4's four-corner layout)
 *
 * Touch is drag-anywhere to move plus double-tap to dash (AMD-48, superseding AMD-1's
 * half-split). On a phone in landscape the player's thumbs sit in the bottom corners
 * for the whole run — which is precisely where §13.1.4 puts the build strip and the
 * dash readout. Both clusters would end up under the player's own hands.
 *
 * So every element the player *reads* moves into one top cluster of opaque plates,
 * and the bottom of the screen carries no chrome at all. The dash button that used to
 * hold the bottom-right corner is gone with the gesture it advertised (AMD-48).
 *
 *   ┌─────────────────────────────────────────────┐
 *   │  ┌ ♥ 128/140 ███████████░ │ 17:01 ┐          │  ← top cluster, plated
 *   │  │ Lv 33 ▁▁▁▁▁  ×7 ▮▮▮▮▯▯▯ │       │          │
 *   │  ├ boss name + bar ─────────────────┤          │
 *   │  └ build ┘                                     │
 *   │                                             │
 *   │             (arena, nothing over it)        │
 *   │                                             │
 *   │            ┌ captain bubble ┐               │  ← z 15, above the band
 *   └─────────────────────────────────────────────┘
 *
 * `EDGE_BAND` px along the left, right and bottom edges stay clear for R6's
 * off-screen threat chevrons (D14). AMD-48 removed the one documented exception, so
 * the guarantee is now unconditional. See `layout.ts` for the full contract.
 *
 * ## Grouping (AMD-26)
 *
 * The plate is organised by the question the datum answers, not by what fits on a
 * line. Three groups, three treatments:
 *
 * - **Survival** — "am I about to die". HP. Loudest numeral, the one long continuous
 *   bar in the HUD, its own row at the top of the plate.
 * - **Power** — "how strong am I". Level + XP, Lumensurge tier + its progress. Under
 *   a hairline, one row, two label-led stats. Neither is a framed bar. The bays and
 *   the Graft (AMD-35 ichor + pity + the pull) are the same question read at a slower
 *   cadence, so they share the one plate below (AMD-43).
 * - **Run** — "how far in am I". The wave and the clock, in their own cell right of a
 *   vertical rule, with the boss bar as the plate directly beneath.
 *
 * Every group states itself the same way: identifier, numeral, then the progress mark
 * that trails it. Nothing is a numeral orphaned from its own bar any more.
 *
 * ## One panel (AMD-42)
 *
 * The grouping above is unchanged; what AMD-42 fixed is everything between and around
 * the groups. Every plate agrees with its neighbours on width, padding, leading and
 * bar scale, so the cluster reads as one instrument rather than two toolbars. Bar
 * *height* carries rank — 12 px for what can end the run, 8 px for structured progress,
 * 4 px for a qualifier trailing its numeral — while shape still carries the category
 * (§11.12). Ichor's mark moved onto the Graft button as its charge, because the counter
 * is the button's price and drawing them apart said otherwise. And every empty state is
 * drawn as a container: eight build bays from the first frame, a hatched vessel at 0/40.
 * Wave 1 has to look like a game that has started, not one that failed to load.
 *
 * ## Two objects, not three (AMD-43)
 *
 * The bays and the Graft were two plates standing on one row, and a row wraps: between
 * 720 px and ~1440 px the Graft dropped under the rack and the cluster read as three
 * unrelated rectangles. They are one plate now, split by the same vertical hairline the
 * main plate puts before its run cell — the Graft is what fills the bays, so containment
 * says what adjacency was only hoping to. One plate cannot wrap into two.
 *
 * The rack was also sized by a different law from the type inside it: bays followed
 * `--hud-scale` (2.77 on a 1080p desktop) while their numerals followed
 * `--ui-text-scale`, which is why a 68 px bay held a 12 px numeral and the rack stood
 * 112 px against the main plate's 74 px. `--mark-scale` caps the first against the
 * second. See `tokens.css`.
 *
 * ## The rack at wave 40 (AMD-46)
 *
 * A build strip that draws the level twice — as a numeral and as a bar the numeral
 * fills — carries exactly one fact per bay, and that fact stops moving at level 5.
 * Wave 40 was eight bays reading `5` over eight full bars: the most interesting build
 * in the run, drawn as the least interesting rack in the run.
 *
 * The numeral still carries the level, because while the level is moving it is the
 * thing you want. What changed is everything that was only repeating it:
 *
 * - **The bar changes denominator at max level.** Under Lv 5 it is progress toward Lv 5.
 *   At Lv 5 it is that weapon's damage against the run's best weapon — the same figure
 *   the Pause recap has always shown, moved to where you can read it without pausing.
 *   Four full bars become four bars of four lengths, and "which of these is carrying the
 *   run" is answerable at a glance. The two meanings never look alike: a share bar is
 *   drawn in the accent tone under a hatch, a progress bar is a plain fill (§11.12 —
 *   shape carries the category, colour only reinforces it).
 * - **Evolved gets its crown.** §13.1.4 asked for a gold crown border and the bay had a
 *   gold *border*: one extra hue on a strip that already tints for max, and nothing at
 *   all for a colourblind preset that keeps the same gold. The bay now grows three
 *   merlons along its top edge, at twice the border weight, and says "evolved" in its
 *   label — shape, weight and text, so gold is the fourth carrier rather than the only
 *   one (§13.4).
 * - **The next evolution announces itself.** The weapon `eligibleEvolutionWeapon` will
 *   put on the guaranteed §9.4.1 card wears shorter, pulsing merlons, and so does the
 *   passive that unlocks it. That pair is the single most actionable thing on the rack
 *   at wave 40: it is not a state you have, it is a state one card away.
 * - **A clipped burst says so.** `budget_trim` fires when §14.1 B3 refuses a split boon's
 *   shards to keep the projectile budget clear for primary fire. Unsurfaced, that reads
 *   as the boon randomly not working. The bay hatches the refused fraction over its own
 *   bar for as long as the trimming continues, and a rate-limited line in the announce
 *   stack spells the count out once — "Mitotic Bloom · 2 of 4 shards".
 *
 * Nothing above adds a rectangle to the cluster or a character to a bay: the rack at
 * 390 px × 150 % text scale has about two pixels of slack (see `ui.css`), so every one
 * of these states is drawn inside the footprint the bay already had.
 *
 * ## Legibility
 *
 * No state is encoded in sub-4px marks. The old 4px pip rows (5 per weapon slot,
 * 10 for Lumensurge) render at 1–2px on a phone; every one of them is now a 4px
 * fill bar paired with a numeral, so the value is readable even when the bar is not.
 *
 * `update()` runs once per rendered frame. It allocates nothing: the whole tree is
 * built in the constructor and every write is guarded by a cached previous value.
 * `measure()` is the only method that touches layout, and it runs on resize, not
 * per frame.
 */

import { bossById, weaponById, passiveById } from '../core/content';
import {
  currentWave,
  currentWaveSegment,
  isBossWave,
  waveProgress,
  waveSegmentRemainingS,
  WAVE_MUSTER_TICKS,
  WAVE_ONSET_TICKS,
  WAVE_SUSTAIN_END_TICKS,
  WAVE_TICKS,
} from '../sim/director';
import type { Enemy } from '../sim/entities';
import { canPull, ichorBalance, ichorFractionCenti, pityProgress, pityThreshold, pullCost } from '../sim/gacha';
import { SURGE_THRESHOLDS } from '../sim/player';
import { eligibleEvolutionWeapon } from '../sim/progression';
import type { World } from '../sim/world';
import { button, div, icon, meter, meterFill, setClass, setMeter, setText, span } from './dom';
import { formatInt, formatRatio, formatTime } from './format';
import { iconFor } from './icons';
import {
  clearRect,
  rect,
  setRect,
  unionInto,
  type HudReservedZones,
  type MutableRect,
  type MutableZones,
} from './layout';

const WEAPON_SLOTS = 4;
const PASSIVE_SLOTS = 4;
const WEAPON_MAX_LEVEL = 5;
const PASSIVE_MAX_RANK = 5;
const SURGE_TIERS = 10;

/**
 * AMD-34 — how far ahead the boss mark looks. Bosses open waves 11/21/31/41, so a
 * decade is always enough to find the next one; past wave 41 there is none and the
 * mark hides rather than pointing at a wave that will never come.
 */
const BOSS_LOOKAHEAD_WAVES = 12;

/** AMD-35 — how long a Graft reveal holds. Long enough to read while still fighting. */
export const GRAFT_REVEAL_MS = 2800;

/** Bays occupied before the rack takes a line of its own at enlarged text. */
const RACK_STACK_AT = 7;

/**
 * AMD-46 — how long a bay stays marked as clipped after the last `budget_trim`.
 *
 * A saturated field trims several times a second, so this is a decay rather than a
 * duration: continuous trimming holds the mark lit, and it fades about a second after
 * the field opens up again. Short enough that a stale hatch never outlives the cause.
 */
export const CLIP_HOLD_MS = 1100;

/**
 * AMD-46 — the clip line's rate limit and hold.
 *
 * The trim event is not rare; the *sentence* has to be. Once every six seconds is enough
 * to answer "why did my boon stop paying out" without turning the announce stack into a
 * ticker, and 2.4 s is the read time of a two-line banner with both thumbs busy.
 */
const CLIP_LINE_GAP_MS = 6000;
const CLIP_LINE_HOLD_MS = 2400;

/**
 * AMD-46 — how often the rack re-asks §9.4.1 which evolution is eligible.
 *
 * `eligibleEvolutionWeapon` walks the weapon slots, walks the passive slots inside that,
 * and hits the content maps on the way. The answer can only change when the build
 * changes, so the build change sets the dirty flag and this is the floor under it — a
 * belt-and-braces re-check twice a second for anything that alters the answer without
 * moving a level or a slot id. `update()` runs on the render path; nothing here may cost
 * a frame what it can cost a half-second.
 */
const RIPE_RECHECK_TICKS = 30;

/**
 * §6.6 boons, by the `source` string their trim carries. The sentence names the boon
 * rather than the weapon, because the boon is the thing the player thinks has broken.
 */
const TRIM_SOURCE_NAMES: Readonly<Record<string, string>> = {
  split: 'Mitotic Bloom',
  cascade: 'Cascade Ganglion',
};

/** The wave bar's three fixed beat boundaries, as percentages of the wave (AMD-34). */
const WAVE_BEAT_PCT: readonly number[] = [
  (WAVE_ONSET_TICKS / WAVE_TICKS) * 100,
  (WAVE_SUSTAIN_END_TICKS / WAVE_TICKS) * 100,
  ((WAVE_TICKS - WAVE_MUSTER_TICKS) / WAVE_TICKS) * 100,
];

/** §13.1.4 low-HP threshold. The HUD is a *redundant* carrier here — §11 owns the
 *  world-space cue, so the survival signal never lives in the HUD alone. */
const CRITICAL_HP_FRAC = 0.3;

/**
 * Reserved edge band for R6's off-screen threat chevrons (D14), in CSS px. Mirrors
 * `--hud-edge` in `ui.css`; the two must move together.
 */
export const EDGE_BAND = 44;

/**
 * True when `.hud-cluster` draws its own plate rather than acting as a layout box.
 *
 * Read from the computed style rather than from a breakpoint constant, so this cannot drift
 * away from the media query that actually decides it. `measure()` runs on resize and on an
 * explicit invalidation, not per frame, so the style read is not on a hot path.
 */
function clusterPaints(node: HTMLElement): boolean {
  if (typeof getComputedStyle !== 'function') return false;
  const cs = getComputedStyle(node);
  if (parseFloat(cs.borderTopWidth) > 0) return true;
  const bg = cs.backgroundColor;
  return bg !== '' && bg !== 'transparent' && !/rgba\(\s*0,\s*0,\s*0,\s*0\s*\)/.test(bg);
}

interface WeaponSlotRefs {
  root: HTMLElement;
  icon: SVGSVGElement;
  level: HTMLElement;
  fill: HTMLElement;
  /** AMD-46 — the refused fraction of a burst, drawn over the right end of the bar. */
  clip: HTMLElement;
}

export class Hud {
  readonly root: HTMLElement;

  /**
   * Fired when the player taps GRAFT. `UI` turns it into a `graft_pull` action — the
   * UI never calls `performPull` itself, because `World` is read-only here
   * (INTERFACES.md ground rule 4).
   */
  onGraft: () => void = () => {};

  private hpFill: HTMLElement;
  private hpText: HTMLElement;
  private barrierWrap: HTMLElement;
  private timerText: HTMLElement;
  private surgeText: HTMLElement;
  private surgeFill: HTMLElement;
  private xpFill: HTMLElement;
  private levelText: HTMLElement;
  private weaponSlots: WeaponSlotRefs[] = [];
  private passiveSlots: WeaponSlotRefs[] = [];
  private plateMain: HTMLElement;
  /** AMD-43 — the one plate the bays and the Graft share. */
  private platePower: HTMLElement;
  private plateBuild: HTMLElement;
  private bossWrap: HTMLElement;
  private bossName: HTMLElement;
  private bossFill: HTMLElement;
  private bossNotches: HTMLElement;
  private introWrap: HTMLElement;
  private introName: HTMLElement;
  private introEpithet: HTMLElement;

  // --- AMD-34 waves ---------------------------------------------------------
  private waveWrap: HTMLElement;
  private waveText: HTMLElement;
  private waveMark: HTMLElement;
  private waveMarkCount: HTMLElement;
  private waveFill: HTMLElement;
  private musterWrap: HTMLElement;
  private musterTitle: HTMLElement;
  private musterSub: HTMLElement;
  private musterCount: HTMLElement;
  private musterCountUnit: HTMLSpanElement;

  // --- AMD-35 the Graft -----------------------------------------------------
  private plateGraft: HTMLElement;
  private ichorText: HTMLElement;
  private ichorFill: HTMLElement;
  private cluster: HTMLElement;
  /** Scratch for the cluster's own box. Module-lifetime, so `measure` allocates nothing. */
  private readonly clusterRect: MutableRect = rect();
  private pityPips: HTMLElement;
  private graftButton: HTMLButtonElement;
  private revealWrap: HTMLElement;
  private revealKind: HTMLElement;
  private revealName: HTMLElement;
  private revealLevel: HTMLElement;
  private revealDelta: HTMLElement;
  private revealCost: HTMLElement;

  // --- AMD-46 the clip line -------------------------------------------------
  private clipWrap: HTMLElement;
  private clipSub: HTMLElement;

  // cached scalars — the frame path only touches the DOM when one of these moves
  private lastHp = -1;
  private lastMaxHp = -1;
  private lastSeconds = -1;
  private lastSurge = -1;
  private lastSurgeQ = -1;
  private lastLevel = -1;
  private lastXpFrac = -1;
  private lastCritical = false;
  private lastWeaponKey: string[] = ['', '', '', ''];
  private lastWeaponLevel: number[] = [-1, -1, -1, -1];
  private lastWeaponEvolved: boolean[] = [false, false, false, false];
  private lastPassiveKey: string[] = ['', '', '', ''];
  private lastPassiveRank: number[] = [-1, -1, -1, -1];
  // --- AMD-46 rack state ----------------------------------------------------
  /** Quantised damage share per weapon bay, so the bar only writes when it moves. */
  private lastWeaponShareQ: number[] = [-1, -1, -1, -1];
  private lastRipeWeapon = '';
  private lastRipePassive = '';
  private ripeDirty = true;
  private ripeCheckAt = 0;
  /** `nowMs` the bay's clip mark expires at; 0 when the bay is not being trimmed. */
  private clipUntilMs: number[] = [0, 0, 0, 0];
  /** Refused fraction of the last trimmed burst, 0..1, per bay. */
  private clipFrac: number[] = [0, 0, 0, 0];
  private clipShown: boolean[] = [false, false, false, false];
  private clipLineUntilMs = 0;
  private clipLineNextMs = 0;
  private lastBossId = '';
  private lastBossFrac = -1;
  private lastWave = -1;
  private lastWaveQ = -1;
  private lastSegment = '';
  private lastMusterCount = -1;
  private lastMusterBoss = false;
  private lastIchor = -1;
  private lastIchorCost = -1;
  private lastIchorQ = -1;
  private lastPity = -1;
  private lastCanPull = false;
  private revealUntilMs = 0;

  /** Resolved once on `boss_spawn`; avoids a per-frame pool scan. */
  private bossRef: Enemy | null = null;
  private bossDefId = '';
  private introUntilMs = 0;

  // --- D8 / D14 occupancy, measured on resize and on visibility flips -------
  private zonesStore: MutableZones;
  private zonesDirty = true;
  private measuredHidden = false;

  constructor() {
    this.root = div('hud');
    this.root.setAttribute('aria-live', 'off');

    // ---------------------------------------------------------------------
    // Top cluster. Everything the player reads lives here, out of both thumb
    // arcs and off the arena.
    // ---------------------------------------------------------------------
    const cluster = div('hud-cluster');
    this.cluster = cluster;

    // -- build strip: 4 weapons + 4 passives, icon + level numeral + fill bar
    this.plateBuild = div('build-bays');
    const weaponRow = div('hud-row weapons');
    for (let i = 0; i < WEAPON_SLOTS; i++) {
      const slot = this.makeSlot('slot weapon', 'shapes');
      weaponRow.appendChild(slot.root);
      this.weaponSlots.push(slot);
    }
    const passiveRow = div('hud-row passives');
    for (let i = 0; i < PASSIVE_SLOTS; i++) {
      const slot = this.makeSlot('slot passive', 'star');
      passiveRow.appendChild(slot.root);
      this.passiveSlots.push(slot);
    }
    this.plateBuild.appendChild(weaponRow);
    this.plateBuild.appendChild(passiveRow);
    this.plateBuild.hidden = true;

    /*
     * -- the power plate: the bays and the Graft, one object (AMD-43).
     *
     * AMD-26 grouped by the question a datum answers, and both of these answer "how
     * strong am I". AMD-35 put them side by side and AMD-42 gave them a shared bottom
     * edge, but they stayed two plates with two borders — and two plates on one row
     * wrap into two rows the moment the row runs out of width, which is what turned
     * the cluster into three floating rectangles between 720 px and ~1440 px.
     *
     * They are one plate now, divided by the same vertical hairline the main plate
     * puts before its run cell. The Graft is the mechanism that *fills* these bays: a
     * pull grants a weapon or a passive and it lands in the rack, and the ichor ratio
     * is the charge on the control that does it. Containment states that; adjacency
     * only hoped for it. A single plate also cannot become two objects at any width,
     * so the wrap that produced the third rectangle is gone by construction rather
     * than by picking a better breakpoint.
     */
    this.platePower = div('hud-plate hud-build');
    this.platePower.appendChild(this.plateBuild);

    this.plateGraft = div('graft-cell');
    const graftReadout = div('graft-readout');

    // Same convention as every other group on the plate: identifier, numeral, then
    // the mark that trails it. `62/120` is balance over the next Graft's price.
    const ichorLine = div('graft-line');
    ichorLine.appendChild(icon('coins', 'ichor-glyph'));
    this.ichorText = span('graft-ichor', '0/40');
    ichorLine.appendChild(this.ichorText);
    graftReadout.appendChild(ichorLine);

    this.plateGraft.appendChild(graftReadout);

    this.graftButton = button('Graft', 'graft-btn', 'seedlings');

    /*
     * AMD-42 — the counter *is* the button's cost, so it is drawn inside the button.
     *
     * The balance and the pull used to be a counter with its own bar on one side of
     * the plate and a greyed button on the other, with `justify-content: space-between`
     * putting as much air between them as the plate had to give. Two widgets, one
     * datum: nothing said the `0/40` was the price of the thing beside it.
     *
     * The hatched fill now runs behind the GRAFT label, from empty to affordable, over
     * a permanently visible hatched track. The ratio beside it names the same fraction
     * in figures. That is containment doing the bonding rather than adjacency hoping
     * to, it deletes a bar form from a HUD that had five of them (§11.12 keeps the
     * hatch, which is ichor's silhouette — it just lives on the control now), and it
     * gives the wave-1 state something to look at: an empty vessel with a visible
     * texture reads as "not yet", where an empty 24 px bar read as "broken".
     */
    this.ichorFill = div('graft-charge');
    this.graftButton.insertBefore(this.ichorFill, this.graftButton.firstChild);

    /*
     * §9.12's pity floor only does its job if the player can see the floor, and the
     * floor is a property of the *pull*, so it rides the pull button rather than
     * sitting in the readout beside the balance. That is also what buys the rail its
     * height: at 390 px x 150 % text scale with a full build, the Graft plate gets
     * 131 px of the cluster, and a second readout line there is 18 px the AMD-8 radar
     * clearance does not have. The reveal banner spells the same counter out in words
     * at the moment it changes.
     */
    this.pityPips = div('pity-pips');
    for (let i = 0; i < pityThreshold(); i++) this.pityPips.appendChild(div('pity-pip'));
    this.graftButton.appendChild(this.pityPips);
    this.graftButton.disabled = true;
    // R9's touch layer skips anything under `[data-ui]`, so this tap cannot be
    // swallowed by the movement stick or read as a Focus Aim drag (`src/input/touch.ts`).
    this.graftButton.dataset.ui = 'graft';
    this.graftButton.addEventListener('click', () => {
      if (!this.graftButton.disabled) this.onGraft();
    });
    this.plateGraft.appendChild(this.graftButton);
    this.platePower.appendChild(this.plateGraft);

    cluster.appendChild(this.platePower);

    // -- main plate: three groups, one per question the player asks (AMD-26).
    //    survival (am I dying) · power (how strong am I) · run (how far in am I)
    this.plateMain = div('hud-plate hud-main');

    const vitals = div('hud-vitals');

    // AMD-51 — `.hud-vitals` is an explicit two-row grid and these are its cells, not a
    // nested column. The run cell on the right subgrids onto the same two rows, which is
    // what puts `WAVE 1` on the survival row's axis and `00:07` on the power row's. With a
    // wrapper in between there was nothing for the run cell to align TO: its contents were
    // centred against the sum of the two rows and sat on a third axis of their own.

    // Group 1 — survival. The only long continuous bar anywhere in the HUD, the
    // only one with a glyph, and the loudest numeral. Label leads, bar trails.
    const hpBlock = div('hud-group hud-hp');
    /*
     * AMD-43 — the glyph and the ratio are one flex item, so the row has exactly two.
     *
     * AMD-42 gave this row a wrap as a valve and drew the shape it wanted: the ratio on
     * the first line, the bar on the second at the full width of the column. With three
     * children the valve had a third position, and on a narrow plate the heart broke off
     * onto a line of its own above `100/100` — a glyph orphaned from the numeral it
     * labels, which is the fault AMD-26 filed as defect 3 in a different place. Two
     * children can only produce the two-line shape.
     */
    const hpHead = div('hp-head');
    hpHead.appendChild(icon('heart', 'hp-glyph'));
    this.hpText = span('hud-hp-text', '100/100');
    hpHead.appendChild(this.hpText);
    hpBlock.appendChild(hpHead);
    const hpMeter = meter('hp');
    this.hpFill = meterFill(hpMeter);
    this.barrierWrap = div('barrier-ticks');
    hpMeter.appendChild(this.barrierWrap);
    hpBlock.appendChild(hpMeter);
    vitals.appendChild(hpBlock);

    // Group 2 — power. Two stats, same shape of statement as survival (label,
    // numeral, then the progress mark) so the eye reads one convention, but
    // neither of them is a framed bar: XP is a flush rule sitting on the type's
    // baseline, Lumensurge is a ten-cell notched track. Shape *and* colour, per
    // §11.12 — the deuteranopia preset puts XP yellow next to surge gold.
    const power = div('hud-group hud-power');

    const levelStat = div('hud-stat hud-xp');
    this.levelText = span('hud-level', 'Lv 1');
    levelStat.appendChild(this.levelText);
    const xpMeter = meter('xp');
    this.xpFill = meterFill(xpMeter);
    levelStat.appendChild(xpMeter);
    power.appendChild(levelStat);

    const surgeBlock = div('hud-stat hud-surge');
    this.surgeText = span('hud-surge-mult', '×1');
    surgeBlock.appendChild(this.surgeText);
    const surgeMeter = meter('surge');
    this.surgeFill = meterFill(surgeMeter);
    surgeBlock.appendChild(surgeMeter);
    power.appendChild(surgeBlock);

    vitals.appendChild(power);

    // Group 3 — run progress. AMD-34 gave this group a second, shorter clock: the
    // wave. It leads the cell because it is now the run's headline unit — you are on
    // wave 27 of 40 the way you were 17 minutes into 20. The cell was already the
    // tallest empty space on the plate (it held one line of type in a two-row grid),
    // so the wave number, its beat-notched bar and the boss mark all cost zero height.
    const timeBlock = div('hud-time');

    this.waveWrap = div('hud-wave');
    this.waveText = span('hud-wave-text', 'WAVE 1');
    this.waveWrap.appendChild(this.waveText);
    // The boss mark: the same crown the event log gives a boss, plus the number of
    // waves until it lands. Visible from a full decade out, which is the whole point
    // — a wave you can see coming is a wave you can pace yourself into.
    this.waveMark = span('hud-wave-mark');
    this.waveMark.appendChild(icon('crown', 'wave-crown'));
    this.waveMarkCount = span('hud-wave-mark-count', '');
    this.waveMark.appendChild(this.waveMarkCount);
    this.waveMark.hidden = true;
    this.waveWrap.appendChild(this.waveMark);

    // AMD-51 — two rows, not three lines.
    //
    // The run cell is as tall as the survival and power rows put together and used to
    // distribute three children against that height, which is why none of them landed on
    // either row. It has two children now and they subgrid onto the plate's own rows:
    // `WAVE 1 ♛11` beside the HP ratio, and the wave's bar beside the clock, beside the
    // power row. That pairing is also the one the left column already uses — row two over
    // there is a numeral with the mark that qualifies it, twice.
    //
    // The bar went on row two rather than beside its own numeral for a measured reason:
    // at 390 px, `WAVE 1 ♛11` plus a legible bar is 148 px of a 302 px plate, which pushed
    // the power row into a wrap and the whole cluster 8 px past §13.1.4's 0.2 H line.
    const runWave = div('hud-run-wave');
    runWave.appendChild(this.waveWrap);
    timeBlock.appendChild(runWave);

    const runTime = div('hud-run-time');

    // The wave bar carries the wave's own shape: three fixed notches at the onset,
    // lull and muster boundaries, so the quiet is a place on the bar rather than a
    // surprise. The fill re-tints per beat (`data-segment` on the wrap).
    const waveMeter = meter('wave');
    this.waveFill = meterFill(waveMeter);
    const beats = div('wave-beats');
    for (let i = 0; i < WAVE_BEAT_PCT.length; i++) {
      const n = div('notch');
      n.style.left = WAVE_BEAT_PCT[i].toFixed(3) + '%';
      beats.appendChild(n);
    }
    waveMeter.appendChild(beats);
    runTime.appendChild(waveMeter);

    this.timerText = span('hud-timer', '00:00');
    runTime.appendChild(this.timerText);
    timeBlock.appendChild(runTime);
    vitals.appendChild(timeBlock);

    this.plateMain.appendChild(vitals);

    cluster.appendChild(this.plateMain);

    // -- boss bar, its own plate directly under the main one: the encounter is the
    //    other half of "how far in am I", so it sits next to the clock, not below
    //    the build strip.
    this.bossWrap = div('hud-plate hud-boss');
    this.bossWrap.hidden = true;
    this.bossName = div('boss-name', '');
    const bossMeter = meter('boss');
    this.bossFill = meterFill(bossMeter);
    this.bossNotches = div('boss-notches');
    bossMeter.appendChild(this.bossNotches);
    this.bossWrap.appendChild(this.bossName);
    this.bossWrap.appendChild(bossMeter);
    cluster.appendChild(this.bossWrap);

    this.root.appendChild(cluster);

    // --- §13.1.7 boss intro framing ---
    this.introWrap = div('boss-intro');
    this.introWrap.hidden = true;
    this.introWrap.appendChild(div('letterbox top'));
    this.introWrap.appendChild(div('letterbox bottom'));
    const plate = div('boss-plate');
    this.introName = div('boss-plate-name', '');
    this.introEpithet = div('boss-plate-epithet', '');
    plate.appendChild(this.introName);
    plate.appendChild(this.introEpithet);
    this.introWrap.appendChild(plate);
    this.root.appendChild(this.introWrap);

    // --- announcements ------------------------------------------------------
    //
    // Both banners hang off the *measured* bottom of the top cluster (`--hud-below-top`,
    // written in `measure()`), which keeps them out of flow. A banner that joined the
    // cluster would grow `zones.top` for three seconds of every thirty and make the
    // AMD-8 radar's clearance flicker; out of flow, the published geometry never moves.
    const announce = div('hud-announce');

    // AMD-34 `muster` — the last 3 s before the swarm lands. The countdown is the
    // point: it is a brace, not a notification.
    this.musterWrap = div('hud-muster');
    this.musterWrap.hidden = true;
    this.musterTitle = div('muster-title', 'INCOMING');
    this.musterSub = div('muster-sub', '');
    /*
     * The count is its own element, and large.
     *
     * The line read "wave 22 in 1" — a bare integer with no unit, in 10 px dim type, inside a
     * brace the player has three seconds to act on. Spelling the unit out fixes the ambiguity
     * and leaves the number buried in a sentence; a blind judge run in both orders preferred
     * pulling it out at high confidence, because "the changing numeral remains legible in
     * peripheral vision" and "gives each tick a clear beat" where the sentence "invites a
     * second look during combat".
     *
     * The unit survives as a graft, subordinated: `s` at micro size and dim beside the
     * numeral, so a player meeting this for the first time is told what is counting without
     * the large read being slowed by it.
     */
    this.musterCount = div('muster-count', '');
    this.musterCountUnit = document.createElement('span');
    this.musterCountUnit.className = 'muster-count-unit';
    this.musterCountUnit.textContent = 's';
    this.musterWrap.appendChild(this.musterTitle);
    this.musterWrap.appendChild(this.musterCount);
    this.musterWrap.appendChild(this.musterSub);
    announce.appendChild(this.musterWrap);

    // AMD-35 Graft reveal. The sim does not pause for this, so it has to be readable
    // with both thumbs busy: four short lines, no motion required to read any of them.
    this.revealWrap = div('hud-reveal');
    this.revealWrap.hidden = true;
    this.revealKind = div('reveal-kind', '');
    const revealHead = div('reveal-head');
    this.revealName = span('reveal-name', '');
    this.revealLevel = span('reveal-level', '');
    revealHead.appendChild(this.revealName);
    revealHead.appendChild(this.revealLevel);
    this.revealDelta = div('reveal-delta', '');
    this.revealCost = div('reveal-cost', '');
    this.revealWrap.appendChild(this.revealKind);
    this.revealWrap.appendChild(revealHead);
    this.revealWrap.appendChild(this.revealDelta);
    this.revealWrap.appendChild(this.revealCost);
    announce.appendChild(this.revealWrap);

    /*
     * AMD-46 — the clipped burst.
     *
     * One line, quieter than the other two, and the last of the three to be raised: a
     * muster is a brace and a Graft reveal is a thing you just bought, while this is an
     * explanation. It never interrupts either — `noteBudgetTrim` declines to raise it
     * while one of them is up rather than stacking a third box into the same anchor.
     *
     * It says the boon's name, not the weapon's. `budget_trim` names the weapon whose
     * bolt burst, but the player is not wondering about the weapon — the weapon is
     * visibly firing. They are wondering why the boon they picked stopped paying, and
     * the answer is that the field is full, which is the second half of the line.
     */
    this.clipWrap = div('hud-clip');
    this.clipWrap.hidden = true;
    this.clipWrap.appendChild(div('clip-title', 'BURST CLIPPED'));
    this.clipSub = div('clip-sub', '');
    this.clipWrap.appendChild(this.clipSub);
    announce.appendChild(this.clipWrap);

    this.root.appendChild(announce);

    // --- redundant low-HP vignette (never the sole carrier, §13.1.4) ---
    this.root.appendChild(div('hud-vignette'));

    this.zonesStore = {
      rects: [rect(), rect(), rect(), rect(), rect()],
      top: rect(),
      edgeBand: EDGE_BAND,
      play: rect(),
      viewport: rect(),
      revision: 0,
    };
  }

  /**
   * One build slot: pixel glyph, level numeral, 4px fill bar.
   *
   * AMD-42 — an unfilled slot is drawn, not hidden. Eight bays exist from the first
   * frame and one of them is full, which is a different statement from "here is one
   * weapon" and a much better one on wave 1: the strip states the size of the build
   * you are working toward instead of growing into a space nobody could see. It also
   * stops the plate resizing every time a pickup lands, which is one less thing that
   * can move the published geometry mid-run.
   */
  private makeSlot(className: string, glyph: string): WeaponSlotRefs {
    const root = div(className + ' is-empty');
    root.setAttribute('aria-hidden', 'true');
    const head = div('slot-head');
    const g = icon(glyph);
    head.appendChild(g);
    const level = span('slot-level', '1');
    head.appendChild(level);
    root.appendChild(head);
    const bar = meter('slot-bar');
    const fill = meterFill(bar);
    // AMD-46 — the clip mark lives *inside* the bar, anchored to its right end, so a
    // trimmed burst takes a visible bite out of the same object it was trimmed from.
    // Built for every bay and left at zero width; a passive bay simply never lights it.
    const clip = div('slot-clip');
    bar.appendChild(clip);
    root.appendChild(bar);
    return { root, icon: g, level, fill, clip };
  }

  // ---------------------------------------------------------------------------
  // D8 / D14 — occupancy published to the render layer
  // ---------------------------------------------------------------------------

  /**
   * Rectangles the HUD paints into, in CSS px viewport coordinates. Read as often
   * as you like: the measurement only re-runs when something moved.
   */
  get zones(): HudReservedZones {
    // `hudVisible` in the capture harness flips `root.hidden` behind our back, so the
    // check is a cheap property read rather than a trusted dirty flag alone.
    if (this.zonesDirty || this.measuredHidden !== this.root.hidden) this.measure();
    return this.zonesStore as HudReservedZones;
  }

  /** Marks the cached geometry stale. Called on resize and on plate visibility flips. */
  invalidateZones(): void {
    this.zonesDirty = true;
  }

  /**
   * Re-reads the HUD's geometry. Layout-thrashing by design, so it runs on resize
   * and on visibility changes only — never inside `update()`.
   */
  measure(): void {
    this.zonesDirty = false;
    this.measuredHidden = this.root.hidden;
    const z = this.zonesStore;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    setRect(z.viewport, 0, 0, vw, vh);
    z.edgeBand = EDGE_BAND;
    z.revision++;

    clearRect(z.top);
    if (this.root.hidden || vw === 0) {
      for (let i = 0; i < z.rects.length; i++) clearRect(z.rects[i]);
      setRect(z.play, 0, 0, vw, vh);
      return;
    }

    this.measureInto(z.rects[0], this.plateMain);
    // AMD-43 — the bays and the Graft are one plate, so slot 1 carries it and slot 4
    // stays a zero-size entry. `layout.ts` pins the array identity, not its occupancy.
    this.measureInto(z.rects[1], this.platePower);
    this.measureInto(z.rects[2], this.bossWrap);
    // AMD-48 — slot 3 held the dash button, the one element the HUD was allowed to
    // paint inside the bottom edge band. The button is gone and the band is now
    // unconditionally clear, so the slot stays a zero-size entry for the same reason
    // slot 4 does: `layout.ts` pins the array identity, not its occupancy.
    clearRect(z.rects[3]);
    clearRect(z.rects[4]);

    unionInto(z.top, z.rects[0]);
    unionInto(z.top, z.rects[1]);
    /*
     * The boss plate is NOT part of the top band, because it is not at the top.
     *
     * `rects[2]` is `.hud-boss`, which AMD-65 anchors to the BOTTOM edge — measured at
     * 390x844 it draws at y 813..832. Unioning it into `top` published a band spanning
     * **y 35 to 832, 797 px of an 844 px screen**, for as long as any boss was alive.
     *
     * Two consumers read that band and both broke on it. AMD-56 docks the radar at the band's
     * bottom edge plus 16, which put it at y 848 — **off the screen entirely, during every
     * boss encounter**, which is the one fight where knowing what is behind you matters most.
     * And §11.9's damage numbers were being kept out of almost the whole playfield.
     *
     * It stays in `rects` — it is still ink a number must not land on — but `top` means the
     * top, and `pushBelowTop` only makes sense for a band a mark can be pushed below.
     */
    unionInto(z.top, z.rects[4]);

    /*
     * The published band is the INK, and on a phone the ink is the cluster's own plate.
     *
     * AMD-26 merged the plates into one box below 420 px: `.hud-cluster` takes the border,
     * the radius and the fill, and its children give theirs up. The rectangles measured above
     * are those children, so the published band stopped 7 px short of what is actually drawn —
     * measured at 390x844, published bottom 161 against a painted bottom of 168.
     *
     * That 7 px has been paid for once already: AMD-56 set the radar's dock gap to 16 px
     * specifically because "the visual plate ends 7 px below the rectangle it publishes". A
     * consumer compensating for a producer's error is the error, twice.
     *
     * Only when the cluster paints. Above 420 px it is a transparent layout box whose height
     * includes gaps and an empty boss slot, and unioning that would publish air as ink.
     */
    if (clusterPaints(this.cluster)) {
      this.measureInto(this.clusterRect, this.cluster);
      unionInto(z.top, this.clusterRect);
    }

    const top = z.top.h > 0 ? z.top.y + z.top.h : 0;
    setRect(z.play, EDGE_BAND, top, Math.max(0, vw - EDGE_BAND * 2), Math.max(0, vh - top - EDGE_BAND));
    // Both announcement banners hang off the cluster's measured bottom edge, so they
    // follow the plates down at 150% text scale without ever entering `top` themselves.
    this.root.style.setProperty('--hud-below-top', Math.round(top) + 'px');
  }

  private measureInto(out: MutableRect, node: HTMLElement): void {
    if (node.hidden || typeof node.getBoundingClientRect !== 'function') {
      clearRect(out);
      return;
    }
    const r = node.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) {
      clearRect(out);
      return;
    }
    setRect(out, r.left, r.top, r.width, r.height);
  }

  // ---------------------------------------------------------------------------

  /** Called from `UI.bindWorld`; resolves the boss entity once per encounter. */
  attachBoss(w: World, entityId: number, bossId: string): void {
    const list = w.enemies.liveIndices();
    for (let i = 0; i < list.length; i++) {
      const e = w.enemies.slots[list[i]];
      if (e.id === entityId) {
        this.bossRef = e;
        break;
      }
    }
    this.bossDefId = bossId;
    this.lastBossId = '';
  }

  /**
   * Drops the tracked encounter. With `bossId`, only when that is the one being tracked.
   *
   * `boss_defeated` used to detach unconditionally, and two bosses can share the arena — the
   * seek path in `Game` guards against spawning a duplicate precisely because "that would put
   * two of them in the arena". Measured on a seek to `boss-10min` at 390x844: the
   * Lantern-Tyrant and the Abyssal Warden were both alive, the Tyrant died, and the bar for
   * the Warden — still alive at 0.74 HP — was torn down with it. `boss_spawn` fired twice,
   * `attachBoss` would have found its entity both times, and the bar showed for 0 of the 833
   * ticks a boss was on the field.
   *
   * The unconditional form was never needed for the death case anyway: `updateBoss` already
   * hides the plate the moment `bossRef.alive` goes false, so a tracked boss dying cleans up
   * on the next frame without help. `reset()` still calls this bare, which is the one place
   * that genuinely means "whatever is tracked, stop".
   */
  detachBoss(bossId?: string): void {
    if (bossId !== undefined && bossId !== this.bossDefId) return;
    this.bossRef = null;
    this.bossDefId = '';
    this.setHiddenTracked(this.bossWrap, true);
    this.lastBossId = '';
  }

  /**
   * §13.1.4 barrier/absorb overlay: segmented white ticks on top of the HP bar.
   * A.4's `Player` carries no absorb field yet, so R1 (or the integrator) drives this
   * when one lands; the HUD renders whatever it is given.
   */
  setBarrier(segments: number, filled: number): void {
    const wrap = this.barrierWrap;
    while (wrap.childElementCount > segments) wrap.removeChild(wrap.lastChild as Node);
    while (wrap.childElementCount < segments) wrap.appendChild(div('barrier-tick'));
    for (let i = 0; i < wrap.childElementCount; i++) {
      setClass(wrap.children[i], 'on', i < filled);
    }
  }

  showBossIntro(name: string, epithet: string, durationMs: number, nowMs: number): void {
    setText(this.introName, name);
    setText(this.introEpithet, epithet);
    this.introWrap.hidden = false;
    this.introWrap.classList.add('is-in');
    this.introUntilMs = nowMs + durationMs;
  }

  /** Read-only pass over `World`. Never mutates the sim (INTERFACES.md rule 4). */
  update(w: World, nowMs: number): void {
    // Geometry used to refresh only when a consumer *read* `zones`. The announcement
    // banners are positioned from `--hud-below-top`, which `measure()` writes, so a
    // frame where nobody read the zones left both banners anchored to a stale cluster
    // bottom and overlapping the plate they hang under. The guard is the same one the
    // getter uses, so this still costs one layout per invalidation, not one per frame.
    if (this.zonesDirty || this.measuredHidden !== this.root.hidden) this.measure();

    const p = w.player;

    if (p.hp !== this.lastHp || p.max_hp !== this.lastMaxHp) {
      this.lastHp = p.hp;
      this.lastMaxHp = p.max_hp;
      const frac = p.max_hp > 0 ? p.hp / p.max_hp : 0;
      setMeter(this.hpFill, frac);
      setText(this.hpText, formatRatio(Math.max(0, Math.ceil(p.hp)), Math.round(p.max_hp)));
      const critical = frac <= CRITICAL_HP_FRAC;
      if (critical !== this.lastCritical) {
        this.lastCritical = critical;
        setClass(this.root, 'is-critical', critical);
      }
    }

    const seconds = Math.floor(w.tick / 60);
    if (seconds !== this.lastSeconds) {
      this.lastSeconds = seconds;
      setText(this.timerText, formatTime(seconds));
    }

    if (p.lumensurge_tier !== this.lastSurge) {
      this.lastSurge = p.lumensurge_tier;
      setText(this.surgeText, '×' + formatInt(p.lumensurge_tier));
    }
    /*
     * One 4px bar carries tier + partial progress toward the next tier.
     *
     * `surge_progress` is a MOTE COUNT — `entities.ts` says so in its own comment, "motes
     * toward next tier" — and this used to feed it through `Math.min(1, …)` as if it were a
     * 0–1 fraction. `SURGE_THRESHOLDS` is [0, 15, 35, 60, …], so tier 1 wants 15 motes: the
     * first mote saturated the sub-tier term and the bar then held still for the other
     * fourteen. Measured before the fix, progress 1, 5 and 50 all drew the identical 20%.
     *
     * Normalised against the tier's own span instead, so the segment fills across the whole
     * climb. `hud.ts` already imports `pityProgress`/`pityThreshold` from the sim to draw the
     * Graft's meter; a threshold table is the same kind of thing and comes from the same place
     * rather than being restated here.
     */
    const tierIdx = Math.max(1, p.lumensurge_tier);
    const lo = SURGE_THRESHOLDS[tierIdx - 1] ?? 0;
    const hi = SURGE_THRESHOLDS[tierIdx] ?? lo;
    const span = hi - lo;
    const within = span > 0 ? Math.max(0, Math.min(1, (p.surge_progress - lo) / span)) : 1;
    const surgeFrac = Math.max(0, Math.min(1, (p.lumensurge_tier + within) / SURGE_TIERS));
    const surgeQ = Math.round(surgeFrac * 200);
    if (surgeQ !== this.lastSurgeQ) {
      this.lastSurgeQ = surgeQ;
      setMeter(this.surgeFill, surgeFrac);
    }

    if (p.level !== this.lastLevel) {
      this.lastLevel = p.level;
      setText(this.levelText, 'Lv ' + formatInt(p.level));
    }
    const xpFrac = p.xp_to_next > 0 ? p.xp / p.xp_to_next : 0;
    const xpQ = Math.round(xpFrac * 200);
    if (xpQ !== this.lastXpFrac) {
      this.lastXpFrac = xpQ;
      setMeter(this.xpFill, xpFrac);
    }

    this.updateBuild(w, nowMs);
    this.updateBoss();
    this.updateWave(w);
    this.updateGraft(w);

    if (this.introUntilMs > 0 && nowMs >= this.introUntilMs) {
      this.introUntilMs = 0;
      this.introWrap.hidden = true;
      this.introWrap.classList.remove('is-in');
    }
    if (this.revealUntilMs > 0 && nowMs >= this.revealUntilMs) {
      this.revealUntilMs = 0;
      this.revealWrap.hidden = true;
      this.revealWrap.classList.remove('is-in');
    }
    if (this.clipLineUntilMs > 0 && nowMs >= this.clipLineUntilMs) {
      this.clipLineUntilMs = 0;
      this.clipWrap.hidden = true;
      this.clipWrap.classList.remove('is-in');
    }
  }

  /**
   * AMD-34 — the wave readout, its beat-notched bar and the muster brace.
   *
   * Everything here is a pure function of `w.tick`, so it survives a mid-run snapshot
   * restore and a replay reproduces it exactly. The `wave_change` / `wave_phase` events
   * feed the log and the audio layer; the HUD reads the queries instead, because a
   * countdown that ticks once per beat is not a countdown.
   */
  private updateWave(w: World): void {
    const wave = currentWave(w);
    if (wave !== this.lastWave) {
      // `WAVE 9` → `WAVE 10` widens the run cell, which moves the plate and therefore
      // the banners hanging off it. A digit is the only thing here that can do that.
      if (this.lastWave < 0 || String(wave).length !== String(this.lastWave).length) this.zonesDirty = true;
      this.lastWave = wave;
      setText(this.waveText, 'WAVE ' + formatInt(wave));
      const boss = isBossWave(wave);
      setClass(this.waveWrap, 'is-boss-wave', boss);
      let until = -1;
      for (let i = 1; i <= BOSS_LOOKAHEAD_WAVES; i++) {
        if (isBossWave(wave + i)) {
          until = i;
          break;
        }
      }
      // On a boss wave the crown is lit and says nothing else; ahead of one it names
      // the wave the crown lands on. After the last encounter there is nothing to
      // point at, so it goes.
      //
      // AMD-42 — the number used to be a countdown, and `WAVE 1 ♛10` states no
      // relationship between its two figures: ten waves away, or the boss on wave 10?
      // Both figures are wave numbers now, in the same face at the same size, so the
      // crown reads as a substitute for the word WAVE and the pair reads as "you are
      // on 1, the crown is on 11". It also confirms itself: on the encounter the two
      // numerals converge and the crown lights, which a countdown never does.
      const show = boss || until > 0;
      if (this.waveMark.hidden === show) this.waveMark.hidden = !show;
      setText(this.waveMarkCount, boss ? '' : until > 0 ? formatInt(wave + until) : '');
      this.waveMark.title = boss
        ? 'Boss wave'
        : until > 0
          ? until === 1
            ? 'Boss on wave ' + (wave + 1) + ', next wave'
            : 'Boss on wave ' + (wave + until) + ', ' + until + ' waves away'
          : '';
      // The wrap's label overrides everything inside it, so it has to carry the crown's
      // sentence too or a screen reader hears "wave 20, 21".
      this.waveWrap.setAttribute(
        'aria-label',
        'Wave ' + wave + (boss ? ', boss wave' : this.waveMark.title ? ', ' + this.waveMark.title.toLowerCase() : ''),
      );
    }

    const frac = waveProgress(w);
    const q = Math.round(frac * 200);
    if (q !== this.lastWaveQ) {
      this.lastWaveQ = q;
      setMeter(this.waveFill, frac);
    }

    const segment = currentWaveSegment(w);
    if (segment !== this.lastSegment) {
      this.lastSegment = segment;
      this.waveWrap.dataset.segment = segment;
    }

    // The muster brace. `remaining_s` runs 3 → 0; a boss muster says a different word
    // in a different colour behind a heavier frame, because bracing for wave 31 is not
    // bracing for wave 28.
    if (segment === 'muster') {
      const bossNext = isBossWave(wave + 1);
      const count = Math.max(1, Math.ceil(waveSegmentRemainingS(w)));
      if (count !== this.lastMusterCount || bossNext !== this.lastMusterBoss) {
        this.lastMusterCount = count;
        this.lastMusterBoss = bossNext;
        setText(this.musterTitle, bossNext ? 'BOSS RISING' : 'INCOMING');
        /*
         * The seconds get their unit. `count` is `ceil(waveSegmentRemainingS)`, so the line
         * read "wave 22 in 1" — a bare integer where every other duration in this project
         * carries one: the run clock is `10:29`, and §13.1.9's report says "0.4 s from ready".
         * Under a three-second brace the reader has no time to work out what the number
         * counts.
         */
        setText(this.musterCount, formatInt(count));
        this.musterCount.appendChild(this.musterCountUnit);
        setText(this.musterSub, 'wave ' + formatInt(wave + 1));
        setClass(this.musterWrap, 'is-boss', bossNext);
      }
      if (this.musterWrap.hidden) {
        this.musterWrap.hidden = false;
        this.musterWrap.classList.add('is-in');
      }
    } else if (!this.musterWrap.hidden) {
      this.musterWrap.hidden = true;
      this.musterWrap.classList.remove('is-in');
      this.lastMusterCount = -1;
    }
  }

  /** AMD-35 — ichor balance, the next Graft's price, the pity floor, the button. */
  private updateGraft(w: World): void {
    const balance = ichorBalance(w);
    const cost = pullCost(w);
    if (balance !== this.lastIchor || cost !== this.lastIchorCost) {
      const next = formatRatio(balance, cost);
      // Same argument as the wave numeral: only a change in *width* can reflow the
      // rail, and only then does the published geometry need re-reading.
      if (next.length !== this.ichorText.textContent?.length) this.zonesDirty = true;
      this.lastIchor = balance;
      this.lastIchorCost = cost;
      setText(this.ichorText, next);
    }
    // The hundredths keep the bar moving between whole credits, which is what makes a
    // trickle read as a trickle rather than as a counter that is stuck.
    const frac = cost > 0 ? Math.min(1, (balance + ichorFractionCenti(w) / 100) / cost) : 0;
    const q = Math.round(frac * 200);
    if (q !== this.lastIchorQ) {
      this.lastIchorQ = q;
      setMeter(this.ichorFill, frac);
    }

    const pity = pityProgress(w);
    if (pity !== this.lastPity) {
      this.lastPity = pity;
      const pips = this.pityPips.children;
      for (let i = 0; i < pips.length; i++) setClass(pips[i], 'on', i < pity);
      const sentence =
        'Graft — pity ' + pity + ' of ' + pityThreshold() + ', an epic is guaranteed at ' + pityThreshold();
      this.graftButton.title = sentence;
      this.graftButton.setAttribute('aria-label', sentence);
    }

    const live = canPull(w);
    if (live !== this.lastCanPull) {
      this.lastCanPull = live;
      this.graftButton.disabled = !live;
      setClass(this.graftButton, 'is-live', live);
      setClass(this.plateGraft, 'is-live', live);
      setClass(this.platePower, 'is-live', live);
      // The balance crossing its price is the one moment this readout has to announce.
      // It is never the sole carrier: the button lights, fills and becomes pressable.
      setClass(this.ichorText, 'is-live', live);
    }
  }

  /** AMD-35 — `gacha_pull` landed. Non-blocking: the sim keeps running behind it. */
  showGraftReveal(
    p: {
      kind: string;
      rarity: string;
      display_name: string;
      delta_text: string;
      level_from: number;
      level_to: number;
      pity_progress: number;
      pity_threshold: number;
      cost: number;
      balance_after: number;
    },
    nowMs: number,
  ): void {
    this.revealWrap.className = 'hud-reveal rarity-' + p.rarity + ' kind-' + p.kind;
    setText(this.revealKind, 'GRAFT · ' + p.rarity);
    setText(this.revealName, p.display_name);
    setText(this.revealLevel, p.level_to > 0 && p.level_to !== p.level_from ? 'Lv ' + p.level_from + ' → ' + p.level_to : '');
    // §13.1.6: the sim owns this sentence. Rendering it verbatim is the contract —
    // a delta re-derived here would drift from the one the sim actually applied.
    setText(this.revealDelta, p.delta_text);
    setText(
      this.revealCost,
      '-' + formatInt(p.cost) + ' ichor · ' + formatInt(p.balance_after) + ' left · pity ' + p.pity_progress + '/' + p.pity_threshold,
    );
    this.revealWrap.hidden = false;
    this.revealWrap.classList.remove('is-in');
    void this.revealWrap.offsetWidth;
    this.revealWrap.classList.add('is-in');
    this.revealUntilMs = nowMs + GRAFT_REVEAL_MS;
  }

  /** AMD-35 — the rising edge of affordability. One pulse, then the button stays lit. */
  pulseGraftReady(): void {
    // AMD-43 — the pulse rides the plate the Graft now lives in, so the one-shot reads
    // as "your build can grow" rather than as a box lighting up beside the bays.
    this.platePower.classList.remove('is-ready-pulse');
    void this.platePower.offsetWidth;
    this.platePower.classList.add('is-ready-pulse');
  }

  /**
   * AMD-46 — a `budget_trim` landed. Called by `UI` from the bus; never by the sim.
   *
   * Two consumers, two lifetimes. The bay's mark is a decay, so a field that keeps
   * trimming keeps it lit and a field that clears drops it about a second later. The
   * line is rate-limited to one every {@link CLIP_LINE_GAP_MS}, because the event is
   * common and the sentence must not be.
   */
  noteBudgetTrim(source: string, weaponId: string, requested: number, spawned: number, nowMs: number): void {
    if (requested <= 0 || spawned >= requested) return;
    const refused = (requested - spawned) / requested;
    for (let i = 0; i < WEAPON_SLOTS; i++) {
      if (this.lastWeaponKey[i] !== weaponId || weaponId === '') continue;
      this.clipUntilMs[i] = nowMs + CLIP_HOLD_MS;
      this.clipFrac[i] = refused;
      break;
    }
    // Never over a brace or a reveal: those two are answering questions the player asked
    // in the last three seconds, and this one is answering a question they have not
    // finished forming. It will fire on the next burst that gets clipped.
    if (nowMs < this.clipLineNextMs) return;
    if (!this.musterWrap.hidden || this.revealUntilMs > 0) return;
    this.clipLineNextMs = nowMs + CLIP_LINE_GAP_MS;
    this.clipLineUntilMs = nowMs + CLIP_LINE_HOLD_MS;
    const boon = TRIM_SOURCE_NAMES[source] ?? source;
    // Kept under ~35 characters on purpose: the announce column is 232 px of usable
    // width at 390 px once AMD-8's radar corridor is reserved, and a wrapped clause
    // leaves a one-word orphan under a centred line.
    setText(this.clipSub, boon + ' · ' + formatInt(spawned) + ' of ' + formatInt(requested) + ' · field full');
    this.clipWrap.hidden = false;
    this.clipWrap.classList.remove('is-in');
    void this.clipWrap.offsetWidth;
    this.clipWrap.classList.add('is-in');
  }

  private updateBuild(w: World, nowMs: number): void {
    const p = w.player;
    let anyVisible = false;

    /*
     * The share denominator: the best weapon in this build, not the run's total damage.
     *
     * Share-of-total would put four bars summing to one full bar's worth of ink, so the
     * whole rack would read short and a two-weapon build would look different from a
     * four-weapon build for reasons that are not about either weapon. Against the best
     * weapon, the leader is always full, everything else is "this fraction of the best
     * thing you have", and the row is a ranking you read in one saccade. It is also the
     * figure the Pause recap has always used, so the two surfaces agree.
     *
     * Four property reads on a `Record`, no key enumeration, no allocation.
     */
    const byWeapon = w.counters.damage_by_weapon;
    // Over *every* filled weapon, not only the maxed ones. A maxed Halo measured against
    // the other maxed weapons would read full while a Lv 4 Lance out-damages it three to
    // one, which is the opposite of what the bar is for.
    let topDamage = 0;
    for (let i = 0; i < p.weapon_count && i < WEAPON_SLOTS; i++) {
      const d = byWeapon[p.weapons[i].def_id];
      if (d !== undefined && d > topDamage) topDamage = d;
    }

    /*
     * §9.4.1's guaranteed evolution slot, asked ahead of the level-up rather than at it.
     * Empty once the run has taken its evolutions, which is correct: nothing is coming.
     *
     * Cached rather than asked per frame. The answer is a pure function of the build, the
     * build changes a few dozen times in twenty minutes, and this runs sixty times a
     * second on the render path.
     */
    let ripeChanged = false;
    if (this.ripeDirty || w.tick >= this.ripeCheckAt) {
      this.ripeDirty = false;
      this.ripeCheckAt = w.tick + RIPE_RECHECK_TICKS;
      const nextWeapon = eligibleEvolutionWeapon(w);
      let nextPassive = '';
      if (nextWeapon !== '') {
        const def = weaponById.get(nextWeapon);
        const linked = def === undefined ? undefined : (def.linked_passive_id as string | undefined);
        if (linked !== undefined) nextPassive = linked;
      }
      ripeChanged = nextWeapon !== this.lastRipeWeapon || nextPassive !== this.lastRipePassive;
      this.lastRipeWeapon = nextWeapon;
      this.lastRipePassive = nextPassive;
    }
    const ripeWeapon = this.lastRipeWeapon;
    const ripePassive = this.lastRipePassive;

    for (let i = 0; i < WEAPON_SLOTS; i++) {
      const slot = this.weaponSlots[i];
      const ws = i < p.weapon_count ? p.weapons[i] : null;
      const id = ws ? ws.def_id : '';
      const keyChanged = id !== this.lastWeaponKey[i];
      if (keyChanged) {
        this.lastWeaponKey[i] = id;
        this.ripeDirty = true;
        this.setSlotFilled(slot.root, id !== '');
        if (id !== '') {
          const use = slot.icon.firstElementChild as SVGUseElement | null;
          const glyph = iconFor(id, 'weapon');
          if (use) use.setAttribute('href', '#i-' + glyph);
        } else {
          this.clipUntilMs[i] = 0;
        }
      }
      if (id !== '') anyVisible = true;

      const maxed = ws !== null && ws.level >= WEAPON_MAX_LEVEL;
      const ripe = id !== '' && id === ripeWeapon;
      const levelChanged = ws !== null && (ws.level !== this.lastWeaponLevel[i] || ws.evolved !== this.lastWeaponEvolved[i]);
      if (levelChanged) {
        this.lastWeaponLevel[i] = ws.level;
        this.lastWeaponEvolved[i] = ws.evolved;
        this.ripeDirty = true;
        setText(slot.level, formatInt(ws.level));
        setClass(slot.root, 'is-evolved', ws.evolved);
        setClass(slot.root, 'is-maxed', maxed);
        // The bar's meaning changes with the level, so the class that says which meaning
        // is on has to change with it too.
        setClass(slot.root, 'is-share', maxed);
        this.lastWeaponShareQ[i] = -1;
      }
      if (keyChanged || levelChanged || ripeChanged) {
        setClass(slot.root, 'is-ripe', ripe);
        if (id !== '') this.labelWeapon(slot.root, id, ws, ripe);
      }

      // The bar: progress under Lv 5, contribution at Lv 5.
      if (ws !== null && id !== '') {
        const frac = maxed
          ? topDamage > 0
            ? Math.min(1, (byWeapon[id] ?? 0) / topDamage)
            : 0
          : Math.min(1, ws.level / WEAPON_MAX_LEVEL);
        const q = Math.round(frac * 200);
        if (q !== this.lastWeaponShareQ[i]) {
          this.lastWeaponShareQ[i] = q;
          setMeter(slot.fill, frac);
        }
      }

      this.updateClipMark(i, slot, nowMs);
    }

    for (let i = 0; i < PASSIVE_SLOTS; i++) {
      const slot = this.passiveSlots[i];
      const ps = i < p.passive_count ? p.passives[i] : null;
      const id = ps ? ps.def_id : '';
      const keyChanged = id !== this.lastPassiveKey[i];
      if (keyChanged) {
        this.lastPassiveKey[i] = id;
        // A passive landing is the other half of what makes an evolution eligible.
        this.ripeDirty = true;
        this.setSlotFilled(slot.root, id !== '');
        if (id !== '') {
          const use = slot.icon.firstElementChild as SVGUseElement | null;
          const glyph = iconFor(id, 'passive');
          if (use) use.setAttribute('href', '#i-' + glyph);
        }
      }
      if (id !== '') anyVisible = true;
      if (ps && ps.rank !== this.lastPassiveRank[i]) {
        this.lastPassiveRank[i] = ps.rank;
        setText(slot.level, formatInt(ps.rank));
        setMeter(slot.fill, Math.min(1, ps.rank / PASSIVE_MAX_RANK));
        setClass(slot.root, 'is-maxed', ps.rank >= PASSIVE_MAX_RANK);
      }
      // A passive with four ranks of the same number in it is not the point of this mark.
      // The point is that *this* passive is the key to the evolution the rack is one card
      // away from, which is the only relationship on the strip that spans both rows.
      if (keyChanged || ripeChanged) {
        const key = id !== '' && id === ripePassive;
        setClass(slot.root, 'is-ripe', key);
        if (id !== '') this.labelPassive(slot.root, id, key);
      }
    }
    if (this.plateBuild.hidden === anyVisible) this.setHiddenTracked(this.plateBuild, !anyVisible);
  }

  /**
   * The bay's clip mark. Width is the refused fraction of the last trimmed burst, and
   * it is the only thing here that runs off wall time rather than off the tick — the
   * trim is a presentation-side explanation, not sim state, so it decays on the clock
   * the player is watching.
   */
  private updateClipMark(i: number, slot: WeaponSlotRefs, nowMs: number): void {
    const on = this.clipUntilMs[i] > nowMs;
    if (on === this.clipShown[i]) return;
    this.clipShown[i] = on;
    setClass(slot.root, 'is-clipped', on);
    slot.clip.style.width = on ? Math.round(this.clipFrac[i] * 100) + '%' : '0%';
  }

  /**
   * A bay's own sentence. Rebuilt on a state change only — never per frame — because it
   * is the carrier §13.4 leans on when the gold and the merlons are both unavailable.
   */
  private labelWeapon(node: HTMLElement, id: string, slot: { level: number; evolved: boolean } | null, ripe: boolean): void {
    const def = weaponById.get(id);
    let text = def ? def.display_name : id;
    if (slot !== null) {
      text += slot.evolved
        ? ' — evolved'
        : slot.level >= WEAPON_MAX_LEVEL
          ? ' — Lv ' + WEAPON_MAX_LEVEL + ', maxed'
          : ' — Lv ' + slot.level + ' of ' + WEAPON_MAX_LEVEL;
    }
    if (ripe) text += ', evolution ready';
    node.title = text;
    node.setAttribute('aria-label', text);
  }

  private labelPassive(node: HTMLElement, id: string, key: boolean): void {
    const def = passiveById.get(id);
    let text = def ? String(def.display_name) : id;
    if (key) text += ' — unlocks the evolution that is ready';
    node.title = text;
    node.setAttribute('aria-label', text);
  }

  private updateBoss(): void {
    const boss = this.bossRef;
    if (!boss || !boss.alive) {
      if (!this.bossWrap.hidden) this.setHiddenTracked(this.bossWrap, true);
      return;
    }
    if (this.bossDefId !== this.lastBossId) {
      this.lastBossId = this.bossDefId;
      const def = bossById.get(this.bossDefId);
      setText(this.bossName, def ? String(def.display_name) : this.bossDefId);
      this.buildNotches(this.bossDefId);
      this.setHiddenTracked(this.bossWrap, false);
    }
    const q = Math.round(boss.hp_frac * 200);
    if (q !== this.lastBossFrac) {
      this.lastBossFrac = q;
      setMeter(this.bossFill, boss.hp_frac);
    }
  }

  /**
   * A bay gains or loses its contents. The plate keeps its size either way, but the
   * level numeral it gains can still widen it, so the geometry is marked stale.
   * `aria-hidden` keeps eight empty boxes out of the screen reader's run.
   */
  /**
   * How many bays are occupied, published to CSS.
   *
   * At enlarged text the rack and the Graft control cannot share a line once the build is
   * large, and the answer — stack them — is only correct once it IS large: measured at 130%,
   * six items cost 138 px side by side and 155 px stacked, while eight cost 202 px side by
   * side and 155 px stacked. A rule that always stacked would fix the full build by taxing
   * every run that never reaches one. CSS cannot count filled children across two rows, so
   * the count is published here and the stylesheet decides what to do with it.
   */
  private publishFilledCount(): void {
    let n = 0;
    for (let i = 0; i < this.weaponSlots.length; i++) {
      if (!this.weaponSlots[i].root.classList.contains('is-empty')) n++;
    }
    for (let i = 0; i < this.passiveSlots.length; i++) {
      if (!this.passiveSlots[i].root.classList.contains('is-empty')) n++;
    }
    const want = n >= RACK_STACK_AT ? 'stack' : 'row';
    if (this.platePower.dataset.rack !== want) this.platePower.dataset.rack = want;
  }

  private setSlotFilled(node: HTMLElement, filled: boolean): void {
    if (node.classList.contains('is-empty') !== filled) return;
    setClass(node, 'is-empty', !filled);
    // The class above is what `publishFilledCount` counts, so it runs after the write.
    queueMicrotask(() => this.publishFilledCount());
    if (filled) {
      node.removeAttribute('aria-hidden');
    } else {
      // A slot that stays on screen keeps whatever skin it was wearing, so emptying
      // one has to strip it — otherwise the next run opens with an evolved-gold bay
      // holding nothing.
      node.setAttribute('aria-hidden', 'true');
      node.removeAttribute('title');
      node.removeAttribute('aria-label');
      setClass(node, 'is-evolved', false);
      setClass(node, 'is-maxed', false);
      // AMD-46 states, stripped for the same reason: a bay that empties must not keep
      // a crown, a share bar or a clip hatch from whatever used to live in it.
      setClass(node, 'is-share', false);
      setClass(node, 'is-ripe', false);
      setClass(node, 'is-clipped', false);
    }
    this.zonesDirty = true;
  }

  /** Any visibility flip moves the published rectangles, so it invalidates them. */
  private setHiddenTracked(node: HTMLElement, hidden: boolean): void {
    if (node.hidden === hidden) return;
    node.hidden = hidden;
    this.zonesDirty = true;
  }

  /** Phase-threshold notches straight from `bosses.json` (§13.1.4). */
  private buildNotches(bossId: string): void {
    while (this.bossNotches.firstChild) this.bossNotches.removeChild(this.bossNotches.firstChild);
    const def = bossById.get(bossId);
    if (!def) return;
    const phases = def.phases as { hp_threshold_pct?: number }[] | undefined;
    if (!Array.isArray(phases)) return;
    for (let i = 0; i < phases.length; i++) {
      const pct = phases[i].hp_threshold_pct;
      if (typeof pct !== 'number' || pct >= 100 || pct <= 0) continue;
      const n = div('notch');
      n.style.left = pct + '%';
      this.bossNotches.appendChild(n);
    }
  }

  /**
   * Full reset between runs.
   *
   * The boss attachment is NOT dropped here, and that is deliberate. `UI.bindWorld` calls
   * this on every re-bind, and a re-bind is not always a new run: `Game.seekRunTime`
   * fast-forwards the world and re-binds the SAME world so the renderer and audio graph can
   * rebuild. The seek's own `boss_spawn` has already fired and attached by then, so resetting
   * the attachment here threw it away one line after `bindWorld`'s comment explaining that
   * exact hazard for the event log.
   *
   * Measured on a seek to `boss-10min` at 390x844: `attachBoss(boss.abyssal-warden)` at
   * t36000, `hud.reset()` at t36000, bar hidden for all 833 ticks a boss was on the field.
   * Callers that mean "new run" call `detachBoss()` themselves; `bindWorld` does it under
   * the `!sameRun` branch that already guards the event log.
   */
  reset(): void {
    this.lastHp = -1;
    this.lastMaxHp = -1;
    this.lastSeconds = -1;
    this.lastSurge = -1;
    this.lastSurgeQ = -1;
    this.lastLevel = -1;
    this.lastXpFrac = -1;
    this.lastCritical = false;
    setClass(this.root, 'is-critical', false);
    this.lastRipeWeapon = '';
    this.lastRipePassive = '';
    this.ripeDirty = true;
    this.ripeCheckAt = 0;
    this.clipLineUntilMs = 0;
    this.clipLineNextMs = 0;
    this.clipWrap.hidden = true;
    this.clipWrap.classList.remove('is-in');
    for (let i = 0; i < WEAPON_SLOTS; i++) {
      this.lastWeaponKey[i] = '';
      this.lastWeaponLevel[i] = -1;
      this.lastWeaponEvolved[i] = false;
      this.lastWeaponShareQ[i] = -1;
      this.clipUntilMs[i] = 0;
      this.clipFrac[i] = 0;
      this.clipShown[i] = false;
      this.weaponSlots[i].clip.style.width = '0%';
      this.setSlotFilled(this.weaponSlots[i].root, false);
    }
    for (let i = 0; i < PASSIVE_SLOTS; i++) {
      this.lastPassiveKey[i] = '';
      this.lastPassiveRank[i] = -1;
      this.setSlotFilled(this.passiveSlots[i].root, false);
    }
    this.plateBuild.hidden = true;
    this.introWrap.hidden = true;
    this.introUntilMs = 0;
    this.lastWave = -1;
    this.lastWaveQ = -1;
    this.lastSegment = '';
    this.lastMusterCount = -1;
    this.lastMusterBoss = false;
    this.lastIchor = -1;
    this.lastIchorCost = -1;
    this.lastIchorQ = -1;
    this.lastPity = -1;
    this.lastCanPull = false;
    this.revealUntilMs = 0;
    this.musterWrap.hidden = true;
    this.musterWrap.classList.remove('is-in');
    this.revealWrap.hidden = true;
    this.revealWrap.classList.remove('is-in');
    this.graftButton.disabled = true;
    setClass(this.graftButton, 'is-live', false);
    setClass(this.plateGraft, 'is-live', false);
    setClass(this.platePower, 'is-live', false);
    setClass(this.ichorText, 'is-live', false);
    this.platePower.classList.remove('is-ready-pulse');
    this.setBarrier(0, 0);
    this.zonesDirty = true;
  }
}
